import { Helmet } from "react-helmet-async";

const SITE = "Mon Diag Énergétique";
const ORIGIN = "https://www.diag-energetique.fr";
const DEFAULT_DESC =
  "Simulez gratuitement votre DPE et optimisez le retour sur investissement de vos travaux";
const OG_IMAGE = `${ORIGIN}/og-image.png`;

type SeoProps = {
  title?: string;
  description?: string;
  /** Chemin canonique relatif (ex. "/" ou "/login"). Defaut: "/". */
  path?: string;
  /** true → noindex,follow (pages privées / sans intérêt d'indexation). */
  noindex?: boolean;
  /** Chemin d'image relatif pour og:image/twitter:image (defaut: /og-image.png). */
  image?: string;
  /**
   * true → utilise `title` brut, sans suffixer " — Mon Diag Énergétique".
   * Réservé à la home (titre Google court et précis) ; les autres routes
   * gardent le suffixe pour contextualiser l'onglet navigateur.
   */
  rawTitle?: boolean;
};

/**
 * Pilote le <head> par route via react-helmet-async.
 * - Routes publiques indexables : noindex = false (défaut) → "index, follow".
 * - Pages privées (auth, dashboard, admin…) : noindex = true → "noindex, follow".
 * Le <title> affiché suit la forme "Page — Mon Diag Énergétique" sauf pour la
 * home qui porte un titre long optimisé SEO.
 */
export function Seo({ title, description, path = "/", noindex = false, image, rawTitle }: SeoProps) {
  const fullTitle = title
    ? rawTitle
      ? title
      : `${title} — ${SITE}`
    : undefined;
  const canonical = `${ORIGIN}${path === "/" ? "/" : path}`;
  const desc = description ?? DEFAULT_DESC;
  const ogImage = image ? `${ORIGIN}${image}` : OG_IMAGE;
  return (
    <Helmet>
      {fullTitle && <title>{fullTitle}</title>}
      <meta name="description" content={desc} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1"} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:site_name" content={SITE} />
      {fullTitle && <meta property="og:title" content={fullTitle} />}
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      {fullTitle && <meta name="twitter:title" content={fullTitle} />}
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}

/** Alias sémantique pour les pages privées (noindex automatique). */
export function NoindexSeo({ title, path }: { title?: string; path?: string }) {
  return <Seo title={title} path={path} noindex />;
}
